import { useField } from 'formik';
import React, { useState } from 'react';

import type {
  SelectAutocompleteProps, SelectAutocompleteItem, SelectAutocompleteResult,
} from '@zimpel/common/components/AutoComplete';
import { SelectAutocomplete } from '@zimpel/common/components/AutoComplete';
import ErrorHint from '@zimpel/common/components/ErrorHint';

type FormikSelectAutocompleteProps = {
  name: string;
  label?: string;
  onGetOptions: (term: string) => Promise<SelectAutocompleteResult>;
  children?: (renderProps: {
    label: string;
    value: string;
    onRemove: (value: string) => void;
  }) => React.ReactNode | string;
} & Omit<SelectAutocompleteProps, 'onSelect'>;

const FormikSelectAutocomplete = ({
  name,
  label = '',
  onGetOptions,
  children = (): React.ReactNode => '',
  ...props
}: FormikSelectAutocompleteProps): any => {
  const [field, meta, helpers] = useField(name);
  const [autocompleteLabel, setAutocompleteLabel] = useState(label);

  const handleSelect = ({ value, label }: SelectAutocompleteItem): void => {
    setAutocompleteLabel(label);
    helpers.setValue(value ? value.toString() : '');
    helpers.setTouched(true);
  };

  const onRemove = (): void => handleSelect({ action: '', value: '', label: '' });

  return (
    field.value ? (
      children({ label: autocompleteLabel, value: field.value, onRemove })
    ) : (
      <>
        <SelectAutocomplete
          onGetOptions={onGetOptions}
          onSelect={handleSelect}
          hasErrors={!!meta.error}
          {...props}
        />
        { meta.error && <ErrorHint>{meta.error}</ErrorHint> }
      </>
    )
  );
};

export default FormikSelectAutocomplete;
export type { FormikSelectAutocompleteProps };
